import React, { FC, useEffect, useState } from 'react';
import { Box, Fade } from '@chakra-ui/react';
import { getAuthorizationTokenForEvent } from 'ts/client/common/clientAuthService';
import useClientEventApiFetch from 'ts/client/common/hooks/useClientEventApiFetch';
import SlideshowPlayerTicker from 'ts/common/components/slideshows/SlideshowPlayerTicker';
import {
    hasBufferedInitialSlides,
    useSlideshowImagePreload
} from 'ts/common/components/slideshows/slideshowImagePreloader';
import {
    HOVER_PREVIEW_START_DEADLINE_MS,
    buildHoverPreviewSlideshow,
    hoverPreviewCoverFrame
} from './slideshowHoverPreview';

interface Props {
    /** Album context for the authorization token, when the grid is inside an album. */
    albumId?: number | string | null;
    /** Photo id of the slideshow tile. */
    photoId: number;
    /** Reports when the excerpt is actually on screen, so the play icon can fade with it. */
    onVisibleChange: (isVisible: boolean) => void;
}

// Matches the 0.25s opacity transition PhotoActionList/PhotoOverlay use for their hover fades.
const FADE_TRANSITION = { enter: { duration: 0.25 }, exit: { duration: 0.25 } };

const FADE_STYLE = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%'
} as const;

/** Successful excerpts only. A miss is not stored, so the next hover can try again. */
const excerptCache = new Map<number, SpApi.IEventSlideshow>();
const EXCERPT_CACHE_LIMIT = 8;

function rememberExcerpt(photoId: number, excerpt: SpApi.IEventSlideshow) {
    if (excerptCache.has(photoId)) {
        excerptCache.delete(photoId);
    }

    excerptCache.set(photoId, excerpt);

    while (excerptCache.size > EXCERPT_CACHE_LIMIT) {
        const oldest = excerptCache.keys().next().value;

        if (oldest === undefined) {
            return;
        }

        excerptCache.delete(oldest);
    }
}

export function clearHoverPreviewCache() {
    excerptCache.clear();
}

function isSlideshowPayload(
    result: { type?: string; slides?: unknown } | null
): result is SpApi.IEventSlideshow {
    return (
        (result?.type === 'event-slideshow' || result?.type === 'event-album-slideshow') &&
        Array.isArray(result.slides)
    );
}

/**
 * Silent looping excerpt of a slideshow, faded in over its tile. Mounted only while this
 * tile holds the grid's single hover slot, so leaving cancels the fetch and the playback
 * together. If the opening slides are not ready before the deadline, nothing is shown and
 * the cover still stays put.
 */
const SlideshowGridItemPreview: FC<Props> = ({ albumId, photoId, onVisibleChange }) => {
    const { performFetch } = useClientEventApiFetch<SpApi.IEventSlideshow>(`photo/${photoId}`, {
        defer: true
    });
    const [excerpt, setExcerpt] = useState<SpApi.IEventSlideshow | null>(
        () => excerptCache.get(photoId) ?? null
    );
    const [gaveUp, setGaveUp] = useState(false);

    useEffect(() => {
        const cached = excerptCache.get(photoId);

        if (cached) {
            setExcerpt(cached);
            return;
        }

        let cancelled = false;
        const tokenRequest =
            albumId !== null && albumId !== undefined && albumId !== '' && albumId !== 'all'
                ? { albumId }
                : undefined;

        (async () => {
            const token = await getAuthorizationTokenForEvent(false, tokenRequest);

            if (cancelled) {
                return;
            }

            let result = await performFetch({
                credentials: 'include',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (cancelled) {
                return;
            }

            if (result?.status === 403) {
                const freshToken = await getAuthorizationTokenForEvent(true, tokenRequest);

                if (cancelled) {
                    return;
                }

                result = await performFetch({
                    credentials: 'include',
                    headers: { Authorization: `Bearer ${freshToken}` }
                });
            }

            if (cancelled || !isSlideshowPayload(result)) {
                return;
            }

            const nextExcerpt = buildHoverPreviewSlideshow(result);

            if (!nextExcerpt) {
                return;
            }

            rememberExcerpt(photoId, nextExcerpt);

            if (!cancelled) {
                setExcerpt(nextExcerpt);
            }
        })().catch(() => undefined);

        return () => {
            cancelled = true;
        };
    }, [albumId, performFetch, photoId]);

    const readyIndices = useSlideshowImagePreload(excerpt?.slides ?? []);
    const imagesReady =
        excerpt !== null && hasBufferedInitialSlides(readyIndices, excerpt.slides.length);
    // Once the deadline passes, a late decode must not pop the preview in. Clearing this
    // timeout as soon as the slides are ready keeps a preview that made it in time on screen.
    const isVisible = imagesReady && !gaveUp;

    useEffect(() => {
        if (imagesReady) {
            return;
        }

        const timeout = setTimeout(() => setGaveUp(true), HOVER_PREVIEW_START_DEADLINE_MS);

        return () => clearTimeout(timeout);
    }, [imagesReady]);

    useEffect(() => {
        onVisibleChange(isVisible);

        return () => onVisibleChange(false);
    }, [isVisible, onVisibleChange]);

    return (
        <Fade in={isVisible} unmountOnExit transition={FADE_TRANSITION} style={FADE_STYLE}>
            {excerpt && (
                <Box
                    position="absolute"
                    top={0}
                    left={0}
                    width="100%"
                    height="100%"
                    overflow="hidden"
                    pointerEvents="none"
                    aria-hidden="true"
                    data-testid="slideshowPreview"
                    sx={{ containerType: 'size' }}
                >
                    <Box
                        position="absolute"
                        top="50%"
                        left="50%"
                        display="flex"
                        transform="translate(-50%, -50%)"
                        data-testid="slideshowPreviewFrame"
                        style={hoverPreviewCoverFrame(excerpt.aspectRatio)}
                    >
                        <SlideshowPlayerTicker
                            slideshow={excerpt}
                            isPlaying
                            muted
                            volume={0}
                            loadingIndicator={null}
                        />
                    </Box>
                </Box>
            )}
        </Fade>
    );
};

export default SlideshowGridItemPreview;
